import {
  CheckCircleLinear,
  DangerCircleLinear,
  DangerTriangleLinear,
  InfoCircleLinear,
} from '@assessprep/icons/solar';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/cn';

// Tinted + iconed callout system. Five variants; each semantic one carries a
// default leading icon. `default` stays neutral + icon-less so the many existing
// neutral callers only retint. Icon API: `icon` prop overrides the per-variant
// default; `icon={null}` hides it; a caller that renders its own leading <svg>
// child suppresses the auto icon (so the two never stack).
const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-muted text-foreground',
        info: 'bg-ap-primary-a10 border-primary/20 text-foreground *:data-[slot=alert-description]:text-foreground',
        success:
          'text-ap-success bg-ap-success-a10 border-ap-success/30 *:data-[slot=alert-description]:text-ap-success/90',
        warning:
          'text-ap-warning bg-ap-warning-a10 border-ap-warning/30 *:data-[slot=alert-description]:text-ap-warning/90',
        destructive:
          'text-destructive bg-ap-destructive-a10 border-ap-destructive-a30 *:data-[slot=alert-description]:text-destructive/90',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const DEFAULT_ICONS: Record<string, React.ReactNode> = {
  info: <InfoCircleLinear aria-hidden="true" />,
  success: <CheckCircleLinear aria-hidden="true" />,
  warning: <DangerTriangleLinear aria-hidden="true" />,
  destructive: <DangerCircleLinear aria-hidden="true" />,
};

export interface AlertProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof alertVariants> {
  /** Leading icon. Defaults to the per-variant icon; pass `null` to hide it. */
  icon?: React.ReactNode;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, icon, children, ...props }, ref) => {
    // A direct child that isn't a title/description is the caller's own leading
    // icon — don't add a second one on top of it.
    const hasChildIcon = React.Children.toArray(children).some(
      (child) =>
        React.isValidElement(child) && child.type !== AlertTitle && child.type !== AlertDescription
    );
    const resolvedIcon =
      icon !== undefined ? icon : hasChildIcon ? null : DEFAULT_ICONS[variant ?? 'default'];

    return (
      <div
        ref={ref}
        data-slot="alert"
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        {resolvedIcon}
        {children}
      </div>
    );
  }
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  )
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed',
        className
      )}
      {...props}
    />
  )
);
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertDescription, AlertTitle };
